/**
 * theme-infra.js
 *
 * Local reimplementation of the "infra" cluster of custom elements that used to
 * be supplied by the remote the vendor bundle (the vendor runtime):
 *
 *   <internal-video>              — self-hosted video player chrome
 *   <section-group>               — relocates two sections into a 2-up grid
 *   <pickup-availability-preview> — inert marker element (behaviour lives in
 *                                   assets/pickup-availability.js)
 *
 * Every selector, class and data attribute used below was read out of this
 * theme's own Liquid / CSS; the source of each one is quoted in a comment.
 *
 * Loading: classic script, `defer`, AFTER assets/secondary.js (it consumes the
 * `isRtl` binding declared there) and after whichever file supplies the shared
 * globals (pauseAllMedia / trapFocus / debounce). Nothing here defines those.
 *
 * The whole file is wrapped in an IIFE so it declares no globals at all and
 * therefore cannot collide with the top-level `const`/`class` bindings in
 * assets/secondary.js (classic scripts share one global lexical scope).
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Local helpers.                                                      *
   * Names are prefixed `infra` so they cannot shadow or collide with the*
   * helpers already defined in assets/secondary.js (debounce, trapFocus,*
   * removeTrapFocus, pauseAllMedia, focusVisiblePolyfill, isRtl).       *
   * ------------------------------------------------------------------ */

  /**
   * Wraps an event handler so a single failure can never bubble out and break
   * unrelated theme JS (e.g. the add-to-cart flow bound elsewhere).
   */
  function infraGuard(fn, context) {
    return function () {
      try {
        return fn.apply(context || this, arguments);
      } catch (error) {
        if (window.console && console.warn) console.warn('[theme-infra]', error);
      }
    };
  }

  /**
   * Reads the RTL flag. `isRtl` is a top-level `const` in assets/secondary.js
   * ("const defaultDirection = ... , isRtl = 'rtl' === defaultDirection"), so it
   * lives in the shared global lexical scope rather than on `window`. Reading it
   * can throw (temporal dead zone) if that file has not executed yet, hence the
   * try/catch plus a DOM fallback. Do NOT redeclare it.
   */
  function infraIsRtl() {
    try {
      if (typeof isRtl !== 'undefined') return !!isRtl;
    } catch (error) {
      /* secondary.js has not run yet — fall through to the DOM check */
    }
    try {
      if (document.documentElement.getAttribute('dir') === 'rtl') return true;
      return window.getComputedStyle(document.documentElement).direction === 'rtl';
    } catch (error) {
      return false;
    }
  }

  /** Runs `fn` once the document has been parsed. */
  function infraWhenReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /** Guarded customElements.define, per the convention in assets/pickup-availability.js. */
  function infraDefine(tagName, constructor) {
    try {
      if (!customElements.get(tagName)) customElements.define(tagName, constructor);
    } catch (error) {
      if (window.console && console.warn) console.warn('[theme-infra] define ' + tagName, error);
    }
  }

  /* ================================================================== *
   * <internal-video>                                                    *
   * ================================================================== *
   *
   * Render sites (verified with `grep -rn '<internal-video'`):
   *   snippets/video-player.liquid:23-70
   *   snippets/product-thumbnail.liquid:135-179
   *   sections/image-with-text.liquid:75-94
   *   sections/icons-with-content.liquid:159-182
   *   sections/results.liquid:70-93
   *   sections/testimonials.liquid:163-191
   *   sections/collapsible-content.liquid:80-103
   *
   * DOM (snippets/video-player.liquid:31-68 is the fullest example):
   *   <internal-video [class="internal-video[ internal-video--playing internal-video--muted]"]
   *                   [data-autoplay="true|false"]
   *                   [data-no-play-btn="true|false"]
   *                   [data-action-on-inactive="none|pause|mute"]>
   *     <video ... [loop] [muted] [autoplay] playsinline disablepictureinpicture>
   *       <source (data-)src="…" type="…">      <- `data-src` whenever autoplay is on
   *     </video>
   *     <button class="internal-video__play">…<div class="play-button …"></div></button>
   *     <div class="internal-video__timeline …">&nbsp</div>          (optional)
   *     <button class="internal-video__sound-btn …"><svg/><svg/></button>  (optional)
   *   </internal-video>
   *
   * NOTE the base `internal-video` class is only emitted by video-player.liquid:24,
   * product-thumbnail.liquid:136 and testimonials.liquid:163 — never key off it,
   * always off the tag name.
   *
   * Cross-component contracts that MUST keep working (assets/secondary.js):
   *   SplideComponent 'hidden'  -> `el.video.pause()` / `el.video.muted = true`
   *                                and reads `el.dataset.actionOnInactive`
   *   SliderComponent           -> `el.querySelector('video')`, `el.dataset.autoplay`
   *   ModalOpener               -> `querySelector('button:not(.internal-video__play, .internal-video__sound-btn)')`
   *   window.pauseAllMedia()    -> pauses our <video> behind our back
   * Because of the last three, the `internal-video--playing` / `internal-video--muted`
   * classes are kept in sync from the MEDIA's own events, never from the click alone.
   */
  var InternalVideoClass = class extends HTMLElement {
    connectedCallback() {
      // The element can be re-connected (section-group relocates content, sliders
      // clone/move slides, section rendering re-inserts markup). Bind only once,
      // but always re-arm autoplay — disconnectedCallback tears its observer down.
      if (this._infraBound) {
        this.setupAutoplay();
        return;
      }
      this._infraBound = true;

      // Children are resolved lazily via getters so that an element upgraded
      // before its children are parsed still works.
      this._playBtn = this.querySelector('.internal-video__play');
      this._timeline = this.querySelector('.internal-video__timeline');
      this._soundBtn = this.querySelector('.internal-video__sound-btn');

      this._onPlayClick = infraGuard(this.handlePlayClick, this);
      this._onSoundClick = infraGuard(this.handleSoundClick, this);
      this._onTimeUpdate = infraGuard(this.updateProgress, this);
      this._onPlay = infraGuard(this.handleMediaPlay, this);
      this._onPause = infraGuard(this.handleMediaPause, this);
      this._onEnded = infraGuard(this.handleMediaEnded, this);
      this._onVolumeChange = infraGuard(this.syncMutedClass, this);
      this._onWaiting = infraGuard(this.handleWaiting, this);
      this._onPlaying = infraGuard(this.handlePlaying, this);

      if (this._playBtn) this._playBtn.addEventListener('click', this._onPlayClick);
      if (this._soundBtn) this._soundBtn.addEventListener('click', this._onSoundClick);

      var video = this.video;
      if (video) {
        video.addEventListener('timeupdate', this._onTimeUpdate);
        video.addEventListener('play', this._onPlay);
        video.addEventListener('pause', this._onPause);
        video.addEventListener('ended', this._onEnded);
        video.addEventListener('volumechange', this._onVolumeChange);
        video.addEventListener('waiting', this._onWaiting);
        video.addEventListener('playing', this._onPlaying);
        video.addEventListener('loadedmetadata', this._onTimeUpdate);
      }

      this.bindTimeline();

      // The autoplay/muted state classes are already baked into the markup at
      // snippets/video-player.liquid:24 and snippets/product-thumbnail.liquid:136.
      // Do NOT add or strip them here — only keep them in sync afterwards.
      this.setupAutoplay();
    }

    disconnectedCallback() {
      // Only tear the IntersectionObserver down. Listeners are intentionally left
      // in place: sliders and <section-group> move these elements around, and a
      // re-connect must not have to re-bind (see the `_infraBound` guard).
      if (this._observer) {
        try {
          this._observer.disconnect();
        } catch (error) {
          /* no-op */
        }
        this._observer = null;
      }
    }

    /**
     * PUBLIC CONTRACT. assets/secondary.js SplideComponent does `e.video.pause()`
     * and `e.video.muted = !0` on the element returned by
     * `t.slide.querySelector("internal-video")`. A getter guarantees it is never
     * undefined, even if the element is upgraded before its children are parsed.
     */
    get video() {
      if (!this._video || !this.contains(this._video)) {
        this._video = this.querySelector('video');
      }
      return this._video;
    }

    get playBtn() {
      return this._playBtn;
    }

    get timeline() {
      return this._timeline;
    }

    get soundBtn() {
      return this._soundBtn;
    }

    /* ------------------------- deferred sources ------------------------- */

    /**
     * Whenever the autoplay flag is on, the Liquid renames `src` to `data-src` on
     * every <source> (snippets/video-player.liquid:47, snippets/product-thumbnail.liquid:156,
     * sections/image-with-text.liquid:83, sections/icons-with-content.liquid:166,
     * sections/results.liquid:77, sections/collapsible-content.liquid:93), so the
     * browser has nothing to load until we copy them across and call load().
     *
     * Needed on BOTH paths: the autoplay start and the first play-button click
     * (video-player.liquid renders the play button even when sources are deferred).
     *
     * @returns {boolean} true if this call actually swapped anything in.
     */
    loadSources() {
      if (this._sourcesLoaded) return false;
      var video = this.video;
      if (!video) return false;

      var deferred = video.querySelectorAll('source[data-src]');
      this._sourcesLoaded = true;
      if (!deferred.length) return false;

      for (var i = 0; i < deferred.length; i++) {
        var source = deferred[i];
        var url = source.getAttribute('data-src');
        if (!url) continue;
        source.setAttribute('src', url);
        source.removeAttribute('data-src');
      }
      video.load();
      return true;
    }

    /* ---------------------------- playback ------------------------------ */

    /**
     * @param {boolean} [forceMuted] mute before playing (required by the browser
     *   autoplay policy, otherwise the returned promise is rejected).
     */
    playVideo(forceMuted) {
      var video = this.video;
      if (!video) return;
      if (forceMuted) video.muted = true;

      var promise;
      try {
        promise = video.play();
      } catch (error) {
        promise = null;
      }
      if (promise && typeof promise.catch === 'function') {
        var self = this;
        promise.catch(function () {
          // Autoplay was blocked (or the media failed). No `pause` event fires in
          // that case, so drop `internal-video--playing` by hand — otherwise
          // `.internal-video--playing .play-button {opacity: 0}` (assets/base.css)
          // leaves the user with no visible way to start the video.
          self.classList.remove('internal-video--playing');
        });
      }
    }

    /**
     * Autoplay start. `data-autoplay` is the literal string "true"/"false"
     * (snippets/video-player.liquid:25 etc.).
     *
     * Three render sites do carry the native `autoplay` attribute
     * (sections/icons-with-content.liquid:170, sections/results.liquid:76,
     * sections/collapsible-content.liquid:87) but their sources are deferred, so
     * the swap + load() is what lets native autoplay fire. The other autoplay
     * sites have no native attribute at all, so we also call play().
     *
     * ASSUMPTION (not derivable from the Liquid — see the report): playback is
     * started ONCE, on the element's first intersection with the viewport, and
     * the observer is then disconnected. We deliberately never auto-pause on
     * scroll-out and never re-play: assets/secondary.js SliderComponent and
     * SplideComponent pause/mute these videos on their own, and a viewport-driven
     * resume would fight them (an off-slide carousel item can still intersect).
     */
    setupAutoplay() {
      if (this.dataset.autoplay !== 'true') return;
      // Idempotent: safe to call on every (re-)connect.
      if (this._autoplayStarted || this._observer) return;

      var self = this;
      var start = function () {
        self._autoplayStarted = true;
        self.loadSources();
        self.playVideo(true);
      };

      if (!('IntersectionObserver' in window)) {
        start();
        return;
      }

      this._observer = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            if (self._observer) {
              self._observer.disconnect();
              self._observer = null;
            }
            start();
            return;
          }
        },
        { rootMargin: '200px 0px' }
      );
      this._observer.observe(this);
    }

    /* ----------------------------- buttons ------------------------------ */

    /**
     * `.internal-video__play` is a bare <button> with no `type` attribute
     * (snippets/video-player.liquid:52), so it defaults to `submit` — preventDefault
     * keeps it from submitting the product form it can sit inside
     * (snippets/product-thumbnail.liquid is rendered under <modal-opener>, and
     * snippets/video-player.liquid is rendered from sections/main-product.liquid:919).
     *
     * When `data-no-play-btn="true"` the Liquid has already applied inline
     * `visibility: hidden` to this button, so it receives no clicks at all. We
     * never touch that inline style.
     */
    handlePlayClick(event) {
      event.preventDefault();
      var video = this.video;
      if (!video) return;

      if (video.paused || video.ended) {
        this.loadSources();
        this.playVideo(false);
      } else {
        video.pause();
      }
    }

    /**
     * `.internal-video__sound-btn` is also a bare <button>
     * (snippets/video-player.liquid:61). stopPropagation because it is stacked
     * above `.internal-video__play` (assets/base.css: z-index 3 vs 1).
     *
     * The icon swap is driven entirely by the `internal-video--muted` class:
     *   .internal-video:not(.internal-video--muted) … svg:nth-child(1) {display:none}
     *   .internal-video.internal-video--muted        … svg:nth-child(2) {display:none}
     * We only flip `video.muted`; the `volumechange` listener syncs the class, so
     * external mutes (SliderComponent / SplideComponent) stay consistent too.
     */
    handleSoundClick(event) {
      event.preventDefault();
      event.stopPropagation();
      var video = this.video;
      if (!video) return;

      this.loadSources();
      video.muted = !video.muted;
      // A muted-autoplay video can have volume 0; restore it so unmuting is audible.
      if (!video.muted && video.volume === 0) video.volume = 1;
    }

    /* ---------------------------- timeline ------------------------------ */

    /**
     * assets/base.css: `.internal-video__timeline::after {width: var(--completed, 0%)}`
     * so the custom property has to be readable from the timeline element itself.
     */
    updateProgress() {
      if (!this._timeline) return;
      var video = this.video;
      var duration = video ? video.duration : 0;
      var percent = 0;
      if (isFinite(duration) && duration > 0) {
        percent = Math.min(100, Math.max(0, (video.currentTime / duration) * 100));
      }
      this._timeline.style.setProperty('--completed', percent + '%');
    }

    /**
     * assets/base.css gives `.internal-video__timeline` `cursor: grab`, i.e. it is
     * a scrubber. assets/rtl.css flips the fill to `right: 0`, so under RTL the
     * pointer→time mapping is mirrored.
     */
    bindTimeline() {
      if (!this._timeline) return;
      var self = this;

      this._scrubbing = false;

      var seek = function (clientX) {
        var video = self.video;
        if (!video || !self._timeline) return;
        var duration = video.duration;
        if (!isFinite(duration) || duration <= 0) return;

        var rect = self._timeline.getBoundingClientRect();
        if (!rect.width) return;

        var ratio = infraIsRtl()
          ? (rect.right - clientX) / rect.width
          : (clientX - rect.left) / rect.width;
        ratio = Math.min(1, Math.max(0, ratio));

        video.currentTime = ratio * duration;
        self.updateProgress();
      };

      if (window.PointerEvent) {
        var onPointerMove = infraGuard(function (event) {
          if (!self._scrubbing) return;
          seek(event.clientX);
        });
        var onPointerUp = infraGuard(function (event) {
          if (!self._scrubbing) return;
          self._scrubbing = false;
          try {
            self._timeline.releasePointerCapture(event.pointerId);
          } catch (error) {
            /* pointer already released */
          }
        });

        this._timeline.addEventListener(
          'pointerdown',
          infraGuard(function (event) {
            // ASSUMPTION: scrubbing does not pause playback (see the report).
            event.preventDefault();
            self._scrubbing = true;

            var captured = false;
            try {
              self._timeline.setPointerCapture(event.pointerId);
              captured = true;
            } catch (error) {
              captured = false;
            }
            // Only fall back to document-level tracking when pointer capture was
            // refused, so a page full of videos does not accumulate listeners.
            if (!captured && !self._documentScrubBound) {
              self._documentScrubBound = true;
              document.addEventListener('pointermove', onPointerMove);
              document.addEventListener('pointerup', onPointerUp);
            }

            seek(event.clientX);
          })
        );
        this._timeline.addEventListener('pointermove', onPointerMove);
        this._timeline.addEventListener('pointerup', onPointerUp);
        this._timeline.addEventListener('pointercancel', onPointerUp);
      } else {
        // Legacy fallback: mouse + touch.
        var onMouseMove = infraGuard(function (event) {
          if (!self._scrubbing) return;
          seek(event.clientX);
        });
        var onMouseUp = infraGuard(function () {
          self._scrubbing = false;
        });

        this._timeline.addEventListener(
          'mousedown',
          infraGuard(function (event) {
            event.preventDefault();
            self._scrubbing = true;
            seek(event.clientX);
          })
        );
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        var touchSeek = infraGuard(function (event) {
          if (!event.touches || !event.touches.length) return;
          seek(event.touches[0].clientX);
        });
        this._timeline.addEventListener('touchstart', touchSeek, { passive: true });
        this._timeline.addEventListener('touchmove', touchSeek, { passive: true });
      }
    }

    /* --------------------------- media events --------------------------- */

    handleMediaPlay() {
      this.classList.add('internal-video--playing');
    }

    handleMediaPause() {
      // Fires for window.pauseAllMedia(), SliderComponent and SplideComponent too,
      // which is exactly why the class is synced here and not on the click.
      this.classList.remove('internal-video--playing');
    }

    /**
     * `ended` does not raise `pause`, so the class has to be dropped explicitly
     * or the play button stays invisible on a non-looping video.
     *
     * ASSUMPTION: currentTime is NOT reset and the poster is not restored — the
     * Liquid does not determine this (see the report).
     */
    handleMediaEnded() {
      this.classList.remove('internal-video--playing');
      this.classList.remove('internal-video--loading');
      this.updateProgress();
    }

    syncMutedClass() {
      var video = this.video;
      if (!video) return;
      this.classList.toggle('internal-video--muted', !!video.muted);
    }

    /**
     * Buffering spinner. assets/base.css `.internal-video--loading::before`.
     * Same convention InstaStories already uses in assets/secondary.js
     * (adds the class in its `waiting` handler, removes it in `playing`).
     */
    handleWaiting() {
      this.classList.add('internal-video--loading');
    }

    handlePlaying() {
      this.classList.remove('internal-video--loading');
      this.classList.add('internal-video--playing');
    }
  };

  infraDefine('internal-video', InternalVideoClass);

  /* ================================================================== *
   * <section-group>                                                     *
   * ================================================================== *
   *
   * Render site: sections/section-group.liquid:24-35, emitted only when both ids
   * are filled (:23). Markup:
   *
   *   <section-group class="… animate-section animate--hidden …"
   *                  data-section-one-id="#shopify-section-…"
   *                  data-section-two-id="#shopify-section-…">
   *     <div class="page-width…">
   *       <div class="section-group-grid section-group-grid-{id}…">
   *         <section class="section-group__section-one-container section-group__container"></section>
   *         <section class="section-group__section-two-container section-group__container"></section>
   *       </div>
   *     </div>
   *   </section-group>
   *
   * Both containers are rendered EMPTY. This element MOVES (never clones) the
   * `.content-for-grouping` element out of each referenced section and into the
   * matching container, then hides the now-empty source wrapper so its
   * `.section {margin-top: …}` (assets/base.css) does not leave a phantom gap.
   *
   * The stored ids come from the per-section copy button, whose payload is
   * uniformly `#shopify-section-{{ section.id }}` (e.g. sections/rich-text.liquid:56),
   * and assets/secondary.js CopyButton copies `dataset.content.trim()` verbatim —
   * so the leading '#' is part of the stored value. The schema fields are free
   * `text` (sections/section-group.liquid:111-115, :126-130), so a bare id or a
   * bare section handle is tolerated too.
   */
  var SectionGroupClass = class extends HTMLElement {
    connectedCallback() {
      this.oneContainer = this.querySelector('.section-group__section-one-container');
      this.twoContainer = this.querySelector('.section-group__section-two-container');

      // records[0] -> container one, records[1] -> container two
      this.records = [null, null];

      var self = this;
      this._runGrouping = infraGuard(this.group, this);

      infraWhenReady(function () {
        // `section-group` may sit before or after its sources in the template, so
        // the move is deferred until the document is parsed.
        if (!self.isConnected) return;
        self._runGrouping();
      });

      // Theme editor: a shopify:section:load on either SOURCE re-renders its
      // content back into its own wrapper, so the grouping has to run again.
      // These are the same events layout/theme.liquid:745-746 and
      // assets/theme-editor.js:28,39 already hook.
      if (window.Shopify && window.Shopify.designMode) {
        this._onEditorEvent = infraGuard(function () {
          // setTimeout(0) so the theme's own shopify:section:load handlers
          // (registered earlier, in the <head>) finish first.
          window.setTimeout(function () {
            if (self.isConnected) self._runGrouping();
          }, 0);
        });
        document.addEventListener('shopify:section:load', this._onEditorEvent);
        document.addEventListener('shopify:section:unload', this._onEditorEvent);
        document.addEventListener('shopify:section:reorder', this._onEditorEvent);
      }
    }

    disconnectedCallback() {
      if (this._onEditorEvent) {
        document.removeEventListener('shopify:section:load', this._onEditorEvent);
        document.removeEventListener('shopify:section:unload', this._onEditorEvent);
        document.removeEventListener('shopify:section:reorder', this._onEditorEvent);
        this._onEditorEvent = null;
      }
      // Hand the borrowed content back so removing/reordering the group in the
      // theme editor does not make the two source sections disappear.
      this.restoreRecord(0);
      this.restoreRecord(1);
    }

    group() {
      this.adopt(this.dataset.sectionOneId, this.oneContainer, 0);
      this.adopt(this.dataset.sectionTwoId, this.twoContainer, 1);

      // Re-prime the scroll animations for the relocated content. initAnimations
      // is declared inline in layout/theme.liquid:727 but only when
      // settings.enable_load_animations is on, hence the typeof guard. Re-observing
      // an already-observed element is a no-op for IntersectionObserver.
      try {
        if (typeof initAnimations === 'function') initAnimations(this);
      } catch (error) {
        /* animations are optional */
      }
    }

    /**
     * Resolves a stored section reference to its `#shopify-section-…` wrapper.
     * getElementById is used rather than querySelector so section ids containing
     * characters that would need CSS escaping (e.g. the `template--123__handle`
     * form used by JSON templates) cannot throw.
     */
    resolveSection(rawValue) {
      if (!rawValue) return null;
      var value = String(rawValue).trim();
      if (!value) return null;
      if (value.charAt(0) === '#') value = value.slice(1);
      if (!value) return null;

      var element = document.getElementById(value);
      if (element) return element;

      // Tolerate a bare id pasted without the wrapper prefix.
      element = document.getElementById('shopify-section-' + value);
      if (element) return element;

      // Last resort: a bare section handle out of a JSON template, whose wrapper
      // id is `shopify-section-template--<numbers>__<handle>`.
      var candidates = document.querySelectorAll('[id^="shopify-section-"]');
      var suffix = '__' + value;
      for (var i = 0; i < candidates.length; i++) {
        var id = candidates[i].id;
        if (id.length > suffix.length && id.indexOf(suffix, id.length - suffix.length) !== -1) {
          return candidates[i];
        }
      }
      return null;
    }

    /**
     * Moves the source's `.content-for-grouping` into `container`.
     *
     * The lookup is scoped to the resolved source element on purpose:
     * sections/pickup-availability.liquid:5 also carries `content-for-grouping`
     * (on <pickup-availability-preview>), so a document-wide search would be a
     * live hazard.
     *
     * No-ops safely when the id is blank/unresolvable or the source has no
     * `.content-for-grouping` (sections/apps.liquid, sections/icons-with-text-2.liquid
     * and sections/main-product.liquid have none) — the container simply stays empty.
     */
    adopt(rawValue, container, slot) {
      if (!container) return;

      var source = this.resolveSection(rawValue);
      if (!source) return;

      var content = source.querySelector('.content-for-grouping');
      if (!content) return;
      if (content.parentNode === container) return; // already grouped, nothing to do

      // A previous run may have parked a now-stale node here: a shopify:section:load
      // on the source replaces its innerHTML, so the node we adopted earlier is a
      // detached leftover. Discard it — restoring it would push a duplicate back
      // into the (still hidden) source wrapper on every editor edit.
      var previous = this.records[slot];
      if (previous && previous.content !== content) {
        this.dropRecord(slot, source);
      }
      while (container.firstChild) container.removeChild(container.firstChild);

      this.records[slot] = {
        content: content,
        parent: content.parentNode,
        next: content.nextSibling,
        source: source,
        // Preserve whatever inline display the wrapper had so it can be restored.
        previousDisplay: source.style.display
      };

      container.appendChild(content);

      // ASSUMPTION: the emptied wrapper is hidden rather than removed — display:none
      // is the reversible choice and keeps the theme editor able to re-render it.
      source.style.display = 'none';
    }

    /**
     * Forgets one adopted node without putting it back — used when the source has
     * re-rendered and the parked node is a stale leftover.
     *
     * @param {number} slot
     * @param {Element} [newSource] the source about to be adopted from; when the
     *   record points at a *different* section that section gets its visibility back.
     */
    dropRecord(slot, newSource) {
      var record = this.records && this.records[slot];
      if (!record) return;
      this.records[slot] = null;

      try {
        if (record.source && record.source !== newSource) {
          record.source.style.display = record.previousDisplay || '';
        }
        if (record.content && record.content.parentNode) {
          record.content.parentNode.removeChild(record.content);
        }
      } catch (error) {
        if (window.console && console.warn) console.warn('[theme-infra] drop', error);
      }
    }

    /** Puts one adopted `.content-for-grouping` back where it came from. */
    restoreRecord(slot) {
      var record = this.records && this.records[slot];
      if (!record) return;
      this.records[slot] = null;

      try {
        if (record.source) record.source.style.display = record.previousDisplay || '';

        var parentStillLive = record.parent && document.contains(record.parent);
        if (parentStillLive && record.content) {
          if (record.next && record.next.parentNode === record.parent) {
            record.parent.insertBefore(record.content, record.next);
          } else {
            record.parent.appendChild(record.content);
          }
        } else if (record.content && record.content.parentNode) {
          // The original wrapper is gone (re-rendered); the parked node is stale.
          record.content.parentNode.removeChild(record.content);
        }
      } catch (error) {
        if (window.console && console.warn) console.warn('[theme-infra] restore', error);
      }
    }
  };

  infraDefine('section-group', SectionGroupClass);

  /* ================================================================== *
   * <pickup-availability-preview>                                       *
   * ================================================================== *
   *
   * Render sites: sections/pickup-availability.liquid:5 and the <template> at
   * snippets/buy-buttons.liquid:226-236.
   *
   * INTENTIONALLY INERT. All of its behaviour already lives in the local
   * assets/pickup-availability.js (loaded by snippets/buy-buttons.liquid:239),
   * which replaces this element's innerHTML in renderPreview() and binds
   * `this.querySelector('button')` itself. Binding anything here would
   * double-bind those buttons. Layout comes purely from the
   * `.pickup-availability-preview` CLASS in assets/component-pickup-availability.css:9-14,
   * which both render sites always emit, so no display fallback is needed either.
   *
   * It is registered only so the tag is a known element (and so that upgrading it
   * — assets/pickup-availability.js:55 assigns fetched outerHTML into innerHTML —
   * can never throw).
   */
  var PickupAvailabilityPreviewClass = class extends HTMLElement {};

  infraDefine('pickup-availability-preview', PickupAvailabilityPreviewClass);
})();
